import {i18n} from '@online-offer/i18n';
import {type MoneyResponseBody} from '@online-offer/shared';
import {Box, Show, Text, VStack} from 'platform/foundation';
import {useFormatCurrency} from 'platform/locale';

import {isNil, isNotEmpty} from 'ramda';

import {EMPTY_PLACEHOLDER, Nullish, suffixTestId, TestIdProps} from 'shared/web';

import {FinancialProductResultRow} from './FinancialProductResultRow';
import {formatFinancialProductMoney} from './formatFinancialProductMoney';
import {formatLeasingCostAmount} from './formatLeasingCostAmount';
import {LeasingCostItem} from './getLeasingCostSections';

interface FinancialProductLeasingIncludesProps extends TestIdProps {
  includes: LeasingCostItem[];
  downPayment: MoneyResponseBody | null;
  termMonths: number | Nullish;
  /** From the displayed quote; a placeholder holds its place while no quote is shown. */
  leasingTotalForTerm: MoneyResponseBody | Nullish;
}

/**
 * The dark panel's account of a leasing with published costs: what the monthly payment includes,
 * then what the customer pays over the whole term, down payment included.
 */
export function FinancialProductLeasingIncludes(props: FinancialProductLeasingIncludesProps) {
  const formatCurrency = useFormatCurrency();

  return (
    <VStack spacing={5} data-testid={suffixTestId('leasingIncludes', props)}>
      <Show when={isNotEmpty(props.includes)}>
        <VStack spacing={3}>
          <Text size="small" color="inverted" alternative>
            {i18n.t('financialProducts.leasingCosts.included.title')}
          </Text>
          <VStack spacing={0}>
            {props.includes.map((item) => (
              <FinancialProductResultRow
                key={item.id}
                label={i18n.t(item.labelKey)}
                value={formatLeasingCostAmount(item, formatCurrency)}
                tooltip={isNil(item.tooltipKey) ? null : i18n.t(item.tooltipKey)}
                isChecked
                data-testid={suffixTestId(`leasingIncludes-${item.id}`, props)}
              />
            ))}
          </VStack>
        </VStack>
      </Show>
      <Box borderTop="thin solid" borderColor="text.inverted" opacity={0.24} />
      <VStack spacing={1}>
        <FinancialProductResultRow
          label={
            isNil(props.termMonths)
              ? i18n.t('financialProducts.totalOver')
              : `${i18n.t('financialProducts.totalOver')} ${i18n.t('financialProducts.monthsValue', {value: props.termMonths})}`
          }
          value={formatFinancialProductMoney(props.leasingTotalForTerm, formatCurrency)}
          emphasized
          data-testid={suffixTestId('leasingTotalForTerm', props)}
        />
        <Show when={props.downPayment}>
          {(downPayment) => (
            <Box opacity={0.62}>
              <Text
                size="xSmall"
                color="inverted"
                data-testid={suffixTestId('leasingTotalForTermNote', props)}
              >
                {`${i18n.t('financialProducts.includingDownPayment')} ${
                  formatFinancialProductMoney(downPayment, formatCurrency) ?? EMPTY_PLACEHOLDER
                }`}
              </Text>
            </Box>
          )}
        </Show>
      </VStack>
    </VStack>
  );
}
